/**
 * usePermissions — Hook for role-based access control in the admin UI.
 *
 * Mirrors useEntitlements pattern but for agent/user permissions.
 * Reads permissions from sidebarData that AdminApp already fetches.
 *
 * Usage:
 *   const { canDo, canManageRole } = usePermissions(sidebarData);
 *   if (canDo('contacts.view')) { ... }
 *   if (canManageRole(50)) { ... } // can manage roles with level > mine
 */

import { useMemo } from 'react';

export default function usePermissions(sidebarData) {
  const permissions = useMemo(
    () => sidebarData?.permissions || {},
    [sidebarData?.permissions],
  );

  const hierarchyLevel = sidebarData?.hierarchy_level ?? 99;
  const roleId = sidebarData?.role_id || null;
  const roleName = sidebarData?.role_name || 'Unknown';

  /**
   * Check if the current user has a specific permission.
   * Owner/Admin (hierarchy_level ≤ 10) always return true.
   * @param {string} permissionKey - e.g. 'contacts.view'
   * @returns {boolean}
   */
  const canDo = (permissionKey) => {
    if (!permissionKey) return false;
    if (hierarchyLevel <= 10) return true;
    return !!permissions[permissionKey];
  };

  /**
   * Check if the current user can manage a role at the given hierarchy level.
   * A user can only manage roles with a HIGHER hierarchy number (lower authority).
   * @param {number} targetHierarchyLevel
   * @returns {boolean}
   */
  const canManageRole = (targetHierarchyLevel) => {
    return hierarchyLevel < targetHierarchyLevel;
  };

  /**
   * Check if the current user is an Owner or Admin (system role with full access).
   * @returns {boolean}
   */
  const isOwnerOrAdmin = hierarchyLevel <= 10;

  /**
   * Check if the current user is the Owner.
   * @returns {boolean}
   */
  const isOwner = hierarchyLevel === 0;

  return {
    canDo,
    canManageRole,
    isOwnerOrAdmin,
    isOwner,
    permissions,
    hierarchyLevel,
    roleId,
    roleName,
  };
}
